import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';

import SwitchField from '@components/Form/SwitchField';
import { useAppContext } from '@contexts/AppContext';
import useUserCreate from '@hooks/useUserCreate';
import useWindowSize from '@hooks/useWindowSize';
import { Theme } from '@interfaces/global';
import { ClickTarget, TypingInput } from '@tracking';
import { WIDTH_SM } from '@utils/constants';
import { LeadTypes } from '@utils/enums';

import EmailForm from '../Form/EmailForm';
import OAuthOption from './OAuthOption';
import { EmailLeadCaptureProps, EmailLeadForm, GDPR_FIELD_LABEL } from './constants';

const EmailLeadCapture = ({
  articleId,
  briefingGuid,
  buttonClassName,
  buttonText = 'Sign up',
  expandableOathButtonsClassName,
  formClassName,
  formLocation,
  gdpr,
  gdprClassName,
  gdprFormClassName,
  includeSignInOption = false,
  inputClassName,
  inputWrapperClassName,
  isNewsletterHeader = false,
  isPro = false,
  isRight = false,
  isVertical = false,
  isVerticalMobile = true,
  leadDetails,
  leadType = LeadTypes.Organic,
  newsletterIds,
  oauthButtonsClassName,
  oauthRedirectToOnSuccess,
  redirectToOnSuccess,
  shareGuid,
  subscribeToAllNewsletters = false,
  signInOptionClass,
  skipVerifyNewEmail,
  theme = Theme.Light,
  triggerNewsletterEvent = false,
  toastMessageOnSuccess,
  unlockContent = false,
  withOauth = false,
  withOnlyOauthExpandable = false,
}: EmailLeadCaptureProps) => {
  const { mutateAsync: createLead, isLoading } = useUserCreate(
    unlockContent,
    leadDetails,
    toastMessageOnSuccess,
    triggerNewsletterEvent
  );
  const { isGdprRequired: contextGdprRequired } = useAppContext();
  const isGdprRequired = gdpr !== undefined ? gdpr : contextGdprRequired;
  const [isGdprOptIn, setIsGdprOptIn] = useState(false);
  const { width: windowWidth } = useWindowSize();
  const shouldBeVertical = isVerticalMobile ? windowWidth < WIDTH_SM : isVertical;

  const handleSubmit = ({ email }: EmailLeadForm) => {
    const createUserRequestParams = {
      article_id: articleId,
      briefing_guid: briefingGuid,
      newsletter_ids: newsletterIds,
      redirect_to_on_success: redirectToOnSuccess,
      subscribe_to_all_newsletters: subscribeToAllNewsletters,
      share_guid: shareGuid,
      skip_verify_new_email: skipVerifyNewEmail,
      user: {
        email: email,
        declined_opt_in: isGdprRequired ? !isGdprOptIn : false,
        opt_in_required: isGdprRequired,
        lead_type: leadType,
        form_location: formLocation,
        page: window.location.href,
      },
    };
    return createLead(createUserRequestParams);
  };

  return (
    <>
      {isGdprRequired && (
        <Formik enableReinitialize initialValues={{ gdprOptIn: false }} onSubmit={() => null}>
          <Form noValidate className={clsx('mb-0 w-full sm:col-span-2', gdprFormClassName)}>
            <SwitchField
              colorTheme={isNewsletterHeader ? Theme.LightAlt : theme}
              className={clsx('gdpr', gdprClassName)}
              switchClassName="gdpr-switch"
              label={GDPR_FIELD_LABEL}
              name="gdprOptIn"
              onChangeCallBack={(optIn) => setIsGdprOptIn(optIn.checked)}
            />
          </Form>
        </Formik>
      )}
      <EmailForm
        buttonText={buttonText}
        formClassName={formClassName}
        gdprClassName={gdprClassName}
        theme={theme}
        isVertical={shouldBeVertical}
        onSubmit={handleSubmit}
        isProcessingSubmission={isLoading}
        inputClassName={inputClassName}
        inputWrapperClassName={inputWrapperClassName}
        buttonClassName={buttonClassName}
        isPro={isPro}
        isRight={isRight}
        includeSignInOption={includeSignInOption}
        signInOptionClass={signInOptionClass}
        trackingClickTarget={ClickTarget.LeadCapture}
        trackingInputTarget={TypingInput.LeadCapture}
      />
      {withOauth && (
        <OAuthOption
          articleId={articleId}
          briefingGuid={briefingGuid}
          expandable={windowWidth < WIDTH_SM || withOnlyOauthExpandable}
          isGdprOptIn={isGdprOptIn}
          isVertical={isVertical}
          leadType={leadType}
          newsletterIds={newsletterIds}
          oauthButtonsClassName={oauthButtonsClassName}
          redirectToOnSuccess={oauthRedirectToOnSuccess || redirectToOnSuccess}
          shareGuid={shareGuid}
          unlockContent={unlockContent}
          expandableOathButtonsClassName={expandableOathButtonsClassName}
        />
      )}
    </>
  );
};

export default EmailLeadCapture;
